import {
  ERROR_LOG_REMOTE_PATH,
  ERROR_LOG_REMOTE_TIMEOUT_MS,
  ERROR_LOG_RTDB_STATUS,
} from '../constants/bookingConst';

// ---------------------------------------------------------------------------
// Sinks two and three: POST to courtbooking-be, which writes every entry to
// the Vercel runtime logs and — only when the database write failed — emails a
// report to the address configured there.
//
// WHY THIS EXISTS ALONGSIDE THE DATABASE SINK:
// /errorLogs is gated on `auth != null`, so it structurally cannot record a
// failure to authenticate: a broken sign-in, an unreachable Auth service, a
// Firebase that never initialised. This path touches no Firebase service at
// all — it is a plain fetch to our own backend — so it still works when every
// other part of the app is dead. That is the whole reason for it.
//
// FIRE AND FORGET, ALWAYS:
// Nothing here is awaited by a caller and nothing here can throw outward. An
// error reporter that breaks the page it is reporting on is worse than no
// reporter, and this one runs inside a catch handler by definition.
// ---------------------------------------------------------------------------

export { ERROR_LOG_RTDB_STATUS } from '../constants/bookingConst';

/**
 * Where to POST. Derived from the email endpoint that every client .env
 * already carries, so this feature needed no .env change across the 18 client
 * deployments — only the backend had to learn the new route.
 *
 * An explicit REACT_APP_ERROR_LOG_ENDPOINT still wins when set, for a client
 * whose backend lives somewhere else.
 *
 * @returns {string|null} absolute URL, or null when neither is configured
 */
const resolveEndpoint = () => {
  const explicit = (process.env.REACT_APP_ERROR_LOG_ENDPOINT || '').trim();
  if (explicit) return explicit;

  const emailBase = (process.env.REACT_APP_EMAIL_ENDPOINT_BASE_URL || '').trim();
  if (!emailBase) return null;

  try {
    // Only the ORIGIN is reused. The email variable points at a nested path
    // (.../api/bookings/confirmation), and string-concatenating onto that
    // would produce a route that does not exist.
    return `${new URL(emailBase).origin}${ERROR_LOG_REMOTE_PATH}`;
  } catch (e) {
    return null;
  }
};

const ENDPOINT = resolveEndpoint();

/** A build with no backend configured simply never sends. */
export const isRemoteLoggingConfigured = () => Boolean(ENDPOINT);

/**
 * Which deployment this is. REACT_APP_FIREBASE_PROJECT_ID rather than
 * REACT_APP_PROJECT_NAME: check-env.js calls the project id the single source
 * of truth because the app cannot connect without it, while PROJECT_NAME is
 * optional and two client branches do not set it at all. A report labelled
 * with a variable that might be missing would be worse than useless.
 *
 * @returns {string}
 */
const clientName = () => (
  (process.env.REACT_APP_PROJECT_NAME || '').trim()
  || (process.env.REACT_APP_FIREBASE_PROJECT_ID || '').trim()
  || 'unknown-client'
);

/** Trims an entry to the fields the backend accepts. */
const toPayload = (entry) => ({
  timestamp: entry.timestamp,
  level: entry.level,
  source: entry.source,
  code: entry.code,
  message: entry.message,
  stack: entry.stack,
  context: entry.context,
  url: entry.url,
  screen: entry.screen,
  userAgent: entry.userAgent,
  sessionId: entry.sessionId,
  actor: entry.actor,
  repeatCount: entry.repeatCount,
});

/**
 * One POST, swallowing everything.
 *
 * `keepalive` is what makes this survive the page it was reporting on: a
 * normal fetch is cancelled when the document unloads, which is exactly when
 * a fatal error tends to be captured. keepalive lets the browser finish the
 * request after teardown, capped at 64kb of body.
 *
 * @param {object} body
 * @returns {void}
 */
const post = (body) => {
  if (!ENDPOINT) return;
  try {
    // Aborted rather than left hanging: a backend that is down should cost a
    // few seconds of a background request, not a socket held for the life of
    // the page.
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    if (controller) setTimeout(() => controller.abort(), ERROR_LOG_REMOTE_TIMEOUT_MS);

    fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clientName: clientName(), ...body }),
      keepalive: true,
      signal: controller ? controller.signal : undefined,
    }).catch(() => {
      // Swallowed on purpose. There is nowhere left to report a failure of the
      // thing that reports failures, and rethrowing would surface as an
      // unhandled rejection — which the capture layer would try to log, which
      // would fail the same way. See the re-entrancy note in errorLogUtil.js.
    });
  } catch (e) {
    // fetch missing or blocked. Nothing to do.
  }
};

/**
 * Sends one entry as it is captured, for the Vercel runtime logs.
 *
 * Sent immediately rather than waiting for the database outcome, because the
 * page may not survive long enough to report it later — and an entry that
 * reaches the log a few seconds before its final status is far better than one
 * that never arrives. `rtdbStatus` carries what is known at this moment:
 * `pending` in the ordinary case, `unavailable` when Firebase never came up.
 *
 * Only `unavailable` triggers an email here; `pending` never does, because the
 * database write has not failed yet.
 *
 * @param {object} entry - the record errorLogUtil built
 * @param {string} rtdbStatus - one of ERROR_LOG_RTDB_STATUS
 * @returns {void}
 */
export const reportErrorToBackend = (entry, rtdbStatus) => {
  if (!entry) return;
  post({ ...toPayload(entry), rtdbStatus });
};

/**
 * Sends the batch the app could not save, once it has genuinely given up.
 *
 * This is what triggers the email. It is a batch and not one message per
 * entry on purpose: when the database sink fails, it usually fails for every
 * error at once (rules not deployed, Auth down), so per-error emails would
 * flood the inbox — and they share an SMTP quota with the booking
 * confirmations. One message carrying every entry says the same thing without
 * putting guest confirmations at risk.
 *
 * @param {Array<object>} entries
 * @param {string} reason - why the database sink gave up, for the email body
 * @param {string} [rtdbStatus] - FAILED when writes were attempted and
 *   rejected, UNAVAILABLE when they could never be attempted at all (no
 *   signed-in user, or Firebase never initialised). Both send the email; the
 *   distinction is what the report says happened.
 * @returns {void}
 */
export const reportFailureBatchToBackend = (
  entries,
  reason,
  rtdbStatus = ERROR_LOG_RTDB_STATUS.FAILED,
) => {
  if (!entries || entries.length === 0) return;
  post({
    entries: entries.slice(0, 60).map(toPayload),
    rtdbStatus,
    reason,
  });
};
